/**
 * ユーザー管理画面 (権限管理 SBI-8)。
 *
 * ControlPanel API の `/users` を使い、ユーザー (実体は Cognito) の一覧 / 招待 / 無効化 / 再有効化 / 削除を行う。
 *
 * - 無効化 / 削除は確認ダイアログを経て実行する。**削除は元に戻せない**。
 * - 自分自身の無効化 / 削除はボタンを無効にして理由を示す (`GET /me/permissions` の `user_sub` と照合)。
 *   拒否自体はバックエンド (400)。最後の管理者の無効化 / 削除 (409) は対処を日本語でバナーに示す。
 * - 無効化しても、発行済みの ID トークンは有効期限 (1 時間) まで使えることを確認ダイアログで案内する。
 *
 * ⚠️ この画面の表示制御 (`user_admin`) は利便性のため。最終的な拒否は常にバックエンド (403)。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type React from 'react';

import { ApiError } from '../api/common';
import type { AdminUser } from '../api/types';
import { UsersClient } from '../api/users';
import { useAuth } from '../auth/AuthProvider';
import type { AppConfig } from '../config';
import { usePermissions } from '../contexts/PermissionContext';
import { groupNamesLabel, userDisplayName, userStatusLabel } from '../permissions/adminLabels';

import { ConfirmDialog } from './ConfirmDialog';
import { NoticeDialog } from './NoticeDialog';
import { describeAdminError } from './permissionAdminMessages';
import { StatusBanner } from './StatusBanner';
import { UserInviteModal } from './UserInviteModal';

export interface UserAdminScreenProps {
  config: AppConfig;
}

/** 確認ダイアログの対象と操作 */
interface PendingAction {
  kind: 'disable' | 'enable' | 'delete';
  user: AdminUser;
}

const ACTION_TEXT: Record<
  PendingAction['kind'],
  { title: string; verb: string; done: string; destructive: boolean }
> = {
  disable: { title: 'ユーザーの無効化', verb: '無効化', done: '無効化', destructive: true },
  enable: { title: 'ユーザーの再有効化', verb: '再有効化', done: '再有効化', destructive: false },
  delete: { title: 'ユーザーの削除', verb: '削除', done: '削除', destructive: true },
};

/** 確認ダイアログの本文 */
function confirmMessage(action: PendingAction): string {
  const name = userDisplayName(action.user);
  if (action.kind === 'delete') {
    return `ユーザー「${name}」を削除します。よろしいですか？\n削除は元に戻せません。所属グループも解除されます。\n(一時的に使えなくするだけなら、無効化を使ってください)`;
  }
  if (action.kind === 'disable') {
    return `ユーザー「${name}」を無効化します。よろしいですか？\n無効化しても、発行済みのログインは最大 1 時間まで使える状態が続きます。`;
  }
  return `ユーザー「${name}」を再有効化します。よろしいですか？`;
}

export function UserAdminScreen({ config }: UserAdminScreenProps): React.JSX.Element {
  const { getIdToken } = useAuth();
  const { userSub: mySub } = usePermissions();
  const apiClient = useMemo(() => new UsersClient({ getIdToken, config }), [getIdToken, config]);

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [working, setWorking] = useState(false);
  const [noticeMessage, setNoticeMessage] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const reload = useCallback(async (): Promise<void> => {
    const currentRequestId = ++requestIdRef.current;
    setLoading(true);
    setErrorMessage(null);
    try {
      const res = await apiClient.list();
      if (currentRequestId !== requestIdRef.current) return;
      setUsers(res.items);
    } catch (err) {
      if (currentRequestId !== requestIdRef.current) return;
      setErrorMessage(describeAdminError(err).message);
    } finally {
      if (currentRequestId === requestIdRef.current) setLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    void reload();
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps -- ref は DOM ではなくリクエスト世代カウンタ
      ++requestIdRef.current;
    };
  }, [reload]);

  const handleInvited = useCallback(
    (user: AdminUser): void => {
      setInviting(false);
      setNoticeMessage(
        `${userDisplayName(user)} を招待しました。招待メールを送信しています。\n\n` +
          '招待直後のユーザーは所属グループがないため、何も利用できません。「権限付与」画面でグループを付与してください。',
      );
      void reload();
    },
    [reload],
  );

  /** 確認ダイアログで確定された操作 (無効化 / 再有効化 / 削除) を実行する。 */
  const handleConfirm = useCallback(async (): Promise<void> => {
    if (!pending) return;
    const { kind, user } = pending;
    setWorking(true);
    setErrorMessage(null);
    try {
      if (kind === 'disable') await apiClient.disable(user.user_sub);
      else if (kind === 'enable') await apiClient.enable(user.user_sub);
      else await apiClient.remove(user.user_sub);
      await reload();
      setNoticeMessage(`ユーザー「${userDisplayName(user)}」を${ACTION_TEXT[kind].done}しました`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        // 先に他者が削除済み: 一覧だけ更新して、削除されていたことを伝える。
        await reload();
        setErrorMessage(describeAdminError(err).message);
      } else {
        setErrorMessage(describeAdminError(err).message);
      }
    } finally {
      setWorking(false);
      setPending(null);
    }
  }, [apiClient, pending, reload]);

  return (
    <div className="permission-admin-page">
      <section className="section" aria-label="ユーザー管理" data-testid="user-admin-screen">
        <div className="route-selector" data-testid="user-admin-toolbar">
          <h2 className="permission-admin-title">ユーザー管理</h2>
          <button
            type="button"
            className="primary create-button"
            data-testid="user-admin-invite"
            onClick={() => setInviting(true)}
          >
            ＋ ユーザーを招待
          </button>
          <div className="actions">
            <button
              type="button"
              data-testid="user-admin-reload"
              onClick={() => void reload()}
              disabled={loading}
            >
              再読み込み
            </button>
          </div>
        </div>
      </section>

      <section className="section permission-admin-list-section">
        <div className="permission-admin-list-body" data-testid="user-admin-list-body">
          {errorMessage ? (
            <StatusBanner
              variant="error"
              onDismiss={() => setErrorMessage(null)}
              data-testid="user-admin-error"
            >
              {errorMessage}
            </StatusBanner>
          ) : null}

          <table className="points-table permission-admin-table" data-testid="user-admin-table">
            <colgroup>
              <col className="permission-admin-col-email" />
              <col className="permission-admin-col-status" />
              <col />
              <col className="permission-admin-col-actions-wide" />
            </colgroup>
            <thead>
              <tr>
                <th>メールアドレス</th>
                <th>状態</th>
                <th>所属グループ</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && !loading ? (
                <tr>
                  <td colSpan={4} className="empty-state" data-testid="user-admin-empty">
                    登録されたユーザーはありません
                  </td>
                </tr>
              ) : null}
              {users.map((user) => {
                const isSelf =
                  mySub !== null && user.user_sub.toLowerCase() === mySub.toLowerCase();
                return (
                  <tr key={user.user_sub} data-testid={`user-admin-row-${user.user_sub}`}>
                    <td>
                      {userDisplayName(user)}
                      {isSelf ? (
                        <span
                          className="permission-admin-self"
                          data-testid={`user-admin-self-${user.user_sub}`}
                        >
                          (自分)
                        </span>
                      ) : null}
                    </td>
                    <td data-testid={`user-admin-status-${user.user_sub}`}>
                      {userStatusLabel(user)}
                    </td>
                    <td data-testid={`user-admin-groups-${user.user_sub}`}>
                      {groupNamesLabel(user.permission_groups)}
                    </td>
                    <td className="row-actions">
                      {user.enabled ? (
                        <button
                          type="button"
                          onClick={() => setPending({ kind: 'disable', user })}
                          disabled={working || isSelf}
                          title={isSelf ? '自分自身は無効化できません' : undefined}
                          data-testid={`user-admin-disable-${user.user_sub}`}
                        >
                          無効化
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setPending({ kind: 'enable', user })}
                          disabled={working}
                          data-testid={`user-admin-enable-${user.user_sub}`}
                        >
                          再有効化
                        </button>
                      )}
                      <button
                        type="button"
                        className="danger"
                        onClick={() => setPending({ kind: 'delete', user })}
                        disabled={working || isSelf}
                        title={isSelf ? '自分自身は削除できません' : undefined}
                        data-testid={`user-admin-delete-${user.user_sub}`}
                      >
                        削除
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {inviting ? (
        <UserInviteModal
          apiClient={apiClient}
          onInvited={handleInvited}
          onClose={() => setInviting(false)}
        />
      ) : null}

      <ConfirmDialog
        open={pending !== null}
        title={pending ? ACTION_TEXT[pending.kind].title : ''}
        message={pending ? confirmMessage(pending) : ''}
        confirmLabel={pending ? ACTION_TEXT[pending.kind].verb : 'OK'}
        destructive={pending ? ACTION_TEXT[pending.kind].destructive : false}
        confirmDisabled={working}
        cancelDisabled={working}
        onConfirm={() => void handleConfirm()}
        onCancel={() => setPending(null)}
      />

      <NoticeDialog
        open={noticeMessage !== null}
        message={noticeMessage ?? ''}
        onClose={() => setNoticeMessage(null)}
      />
    </div>
  );
}
